import { Globe, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { getRuntimeConfig } from '@nucleus-commerce/admin-kit/config';
import type { SocialConnection } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Alert, AlertDescription, AlertTitle } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';

import { ChannelStep, StepBadge, StepRow } from './channel-step';

export interface WebsiteStepProps {
  /** False until the network is connected; the step then only says so. */
  active: boolean;
  connection: SocialConnection | undefined;
  /** The store's primary domain; undefined while the store detail loads. */
  primaryDomain: string | null | undefined;
  editable: boolean;
  claiming: boolean;
  onClaim: () => void;
  reconnecting: boolean;
  onReconnect: () => void;
}

/** The one storefront change a claim needs: forward the network's file path to the API. */
function verificationRewrite(apiBaseUrl: string): string {
  const api = apiBaseUrl.replace(/\/+$/, '');
  return JSON.stringify(
    {
      source: '/:file(pinterest-[a-zA-Z0-9_-]+\\.html)',
      has: [{ type: 'host', value: '(?<host>.+)' }],
      destination: `${api}/site-verification/:host/:file`,
    },
    null,
    2,
  );
}

/** Step 2: claim the storefront's domain on the account, which the catalogue step needs. */
export function WebsiteStep({
  active,
  connection,
  primaryDomain,
  editable,
  claiming,
  onClaim,
  reconnecting,
  onReconnect,
}: WebsiteStepProps) {
  const { t } = useTranslation('social');
  const format = useFormat();
  const website = connection?.website ?? null;
  const status = website?.status ?? null;
  const domain = website?.domain ?? primaryDomain ?? null;

  const badge = !active ? (
    <StepBadge tone="todo">{t('steps.waiting')}</StepBadge>
  ) : status === 'verified' ? (
    <StepBadge tone="done">{t('website.claimed')}</StepBadge>
  ) : status === 'failed' ? (
    <StepBadge tone="attention">{t('website.notClaimed')}</StepBadge>
  ) : status === 'pending' ? (
    <StepBadge tone="todo">{t('website.pending')}</StepBadge>
  ) : (
    <StepBadge tone="todo">{t('website.notClaimed')}</StepBadge>
  );

  const claimButton = (
    <Button
      type="button"
      size="sm"
      variant={status === 'verified' ? 'outline' : 'default'}
      disabled={claiming}
      onClick={onClaim}
    >
      {status ? (
        <RefreshCw aria-hidden className={claiming ? 'animate-spin' : undefined} />
      ) : (
        <Globe aria-hidden />
      )}
      {claiming ? t('website.claiming') : status ? t('website.checkAgain') : t('website.claim')}
    </Button>
  );

  return (
    <ChannelStep index={2} title={t('website.title')} badge={badge} disabled={!active}>
      <p className="text-muted-foreground text-sm">{t('website.hint')}</p>
      {!active ? null : (
        <>
          <dl className="grid gap-3 sm:grid-cols-2">
            <StepRow label={t('website.domain')}>
              {domain ? <span className="font-mono text-xs">{domain}</span> : '—'}
            </StepRow>
            {website?.verifiedAt ? (
              <StepRow label={t('website.verifiedAt')}>
                {format.dateTime(website.verifiedAt)}
              </StepRow>
            ) : website?.checkedAt ? (
              <StepRow label={t('website.checkedAt')}>{format.dateTime(website.checkedAt)}</StepRow>
            ) : null}
          </dl>
          {status === 'failed' && website ? (
            <Alert variant="destructive" role="alert">
              <AlertTitle>
                {t(`website.errors.${website.error ?? 'provider_rejected'}.title`)}
              </AlertTitle>
              <AlertDescription className="flex flex-col gap-2">
                <span>
                  {t(`website.errors.${website.error ?? 'provider_rejected'}.body`, {
                    domain: website.domain,
                    username: connection?.username ?? '',
                  })}
                </span>
                {website.message ? <span className="text-xs">{website.message}</span> : null}
                {website.error === 'not_forwarded' ? (
                  <>
                    <span className="text-xs">{t('website.rewriteHint')}</span>
                    <pre className="bg-muted text-foreground overflow-x-auto rounded-md px-2 py-1.5 font-mono text-xs select-all">
                      {verificationRewrite(getRuntimeConfig().apiBaseUrl)}
                    </pre>
                  </>
                ) : null}
                {website.error === 'claimed_elsewhere' ? (
                  <>
                    <span className="text-xs">{t('website.claimedElsewhereHint')}</span>
                    {editable ? (
                      <div>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={reconnecting}
                          onClick={onReconnect}
                        >
                          <RefreshCw aria-hidden />
                          {reconnecting
                            ? t('connection.connecting')
                            : t('website.reconnectElsewhere')}
                        </Button>
                      </div>
                    ) : null}
                  </>
                ) : null}
              </AlertDescription>
            </Alert>
          ) : null}
          {status === 'pending' ? (
            <p className="text-muted-foreground text-xs">{t('website.pendingHint')}</p>
          ) : null}
          {!editable ? null : connection && !connection.canClaimWebsite ? (
            <div className="flex flex-col gap-2">
              <p className="text-sm">{t('website.needsPermission')}</p>
              <div>
                <Button type="button" size="sm" disabled={reconnecting} onClick={onReconnect}>
                  <RefreshCw aria-hidden />
                  {reconnecting ? t('connection.connecting') : t('website.reconnect')}
                </Button>
              </div>
            </div>
          ) : primaryDomain === null && !website ? (
            <p className="text-sm">{t('website.noDomain')}</p>
          ) : (
            <div>{claimButton}</div>
          )}
        </>
      )}
    </ChannelStep>
  );
}
